import React, { useContext, useState, useEffect } from "react";
import SharedDataContext from "../../../SharedDataContext";
import RepStatsTable from "./components/RepStatsTable";
import { getCallActivityRepStats } from "../../../apis/callActivity";
import { useQueryParams } from "../../../hooks/useQueryParams";
import useUpdateQueryParam from "../../../hooks/useUpdateQueryParam";
import { useSmartQuery } from "../../../apiHooks/useSmartQuery";
import { getActivityFilterCountries, getActivityFilterTimezones } from "../../../apis/callActivity";
import { countries } from "countries-list";
import { getCountryFlag } from "../../../constants/countries";
import Flag from "../../Flag";
import Image from "../../Tags/Image";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Button } from "../../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import { cn } from "../../../lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function RepLevelTab({ filters, hasAccess = true }) {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const queryParams = useQueryParams();
  const updateQueryParam = useUpdateQueryParam();

  const [selectedCountry, setSelectedCountry] = useState(queryParams.country || "US");
  const [selectedTimezone, setSelectedTimezone] = useState(queryParams.timezone || null);
  
  const [countryOpen, setCountryOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);

  useEffect(() => {
    if (selectedCountry) {
      updateQueryParam("country", selectedCountry);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedTimezone) {
      updateQueryParam("timezone", selectedTimezone);
    }
  }, [selectedTimezone]);

  const { data: countriesData } = useSmartQuery(
    ["activity_filter_countries", activeWorkspace?.id],
    () =>
      getActivityFilterCountries({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id,
    }
  );

  const { data: timezonesData } = useSmartQuery(
    ["activity_filter_timezones", activeWorkspace?.id, selectedCountry],
    () =>
      getActivityFilterTimezones({
        countryCode: selectedCountry || "US",
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id && !!(selectedCountry || "US"),
    }
  );

  // Removed auto-select logic for timezone to allow 'All' (null) state

  const getCountryName = (code) => {
    if (!code) return code;
    const countryData = countries[code.toUpperCase()];
    return countryData?.name || code;
  };

  const timezoneNames = {
    "America/Los_Angeles": "Pacific Time (PT)",
    "America/Denver": "Mountain Time (MT)",
    "America/Chicago": "Central Time (CT)",
    "America/New_York": "Eastern Time (ET)",
    "Europe/London": "Greenwich Mean Time (GMT)",
    "Europe/Paris": "Central European Time (CET)",
    "Asia/Kolkata": "India Standard Time (IST)",
    "Asia/Dubai": "Gulf Standard Time (GST)",
    "Australia/Sydney": "Australian Eastern Time (AET)",
  };

  const availableCountries = countriesData?.data || ["US", "IN"];
  const countriesMap = {};
  availableCountries.forEach((code) => {
    countriesMap[code] = getCountryName(code);
  });

  const availableTimezones = timezonesData?.data || ["America/Los_Angeles"];
  const timezonesMap = {};
  availableTimezones.forEach((tz) => {
    timezonesMap[tz] = timezoneNames[tz] || tz;
  });

  const renderFlag = (countryCode) => {
    if (!countryCode) return getCountryFlag("US");
    return <Flag code={countryCode} className="rounded-full" />;
  };

  // Build query params for the API
  const buildQueryParams = () => {
    const params = new URLSearchParams();

    // Time Range
    if (filters.dateRange) {
      if (filters.dateRange === "custom") {
        params.append("time_range", "custom");
        if (filters.customStartDate) {
          params.append("start_date", filters.customStartDate);
        }
        if (filters.customEndDate) {
          params.append("end_date", filters.customEndDate);
        }
      } else {
        const dateRangeMap = {
          "7 days": "7d",
          "30 days": "30d",
          "60 days": "60d",
          "90 days": "90d",
          "180 days": "180d",
          "1 year": "1y",
        };
        params.append("time_range", dateRangeMap[filters.dateRange] || "30d");
      }
    }

    // Call Direction
    if (filters.callDirection && filters.callDirection !== "All") {
        const callDirectionMap = {
          outbound: "Outgoing",
          inbound: "Incoming",
          Outgoing: "Outgoing",
          Incoming: "Incoming",
        };
        // The API seems to expect 'Outgoing' or 'Incoming' based on user request example
        // curl example used: call_direction=Outgoing
        params.append(
          "call_direction",
          callDirectionMap[filters.callDirection] || filters.callDirection
        );
      }

    // Country
    if (selectedCountry) {
        params.append("country", selectedCountry);
    }

    // Timezone
    if (selectedTimezone) {
        params.append("timezone", selectedTimezone);
    }

    return params.toString();
  };

  const apiQueryParams = buildQueryParams();
  
  // Determine Team ID
  const teamId = filters.viewToggle === "Team" && filters.userTeamSelector 
    ? filters.userTeamSelector 
    : undefined;

  const fetchRepStats = ({ query, options, additionalQuery }) => {
    // Combine the table's query (pagination/sort) with our global filters (additionalQuery)
    const parts = [];
    if (query) parts.push(query);
    if (additionalQuery) parts.push(additionalQuery);
    const combinedQuery = parts.join("&");
    
    const mergedOptions = {
        ...options,
        headers: {
            ...options?.headers,
            "X-Workspace-Id": activeWorkspace?.id,
        }
    };

    return getCallActivityRepStats({
        teamId: teamId,
        query: combinedQuery,
        options: mergedOptions
    });
  };

  const [graphData, setGraphData] = useState([]);

  // Find max value for scaling graph
  const maxCallVolume = Math.max(...(graphData.map(d => d.call_volume) || [0]), 1);

  return (
    <div className="space-y-6">
      {/* Filters Row */}
      <div className="flex items-center gap-4">
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
            <PopoverTrigger asChild>
                <Button
                variant="outline"
                role="combobox"
                aria-expanded={countryOpen}
                className="justify-between h-10 rounded-lg border-gray-200 hover:border-gray-300 bg-white dark:bg-[#0C0E12] dark:border-gray-200 dark:hover:border-gray-300 dark:text-white"
                >
                <div className="flex items-center gap-2">
                    <span className="text-lg inline-flex items-center justify-center w-6 h-4 rounded-full overflow-hidden">
                    {renderFlag(selectedCountry || Object.keys(countriesMap)[0] || "US")}
                    </span>
                    <span className="text-sm text-[#414651] dark:text-white font-semibold">
                    {countriesMap[selectedCountry] ||
                        countriesMap[Object.keys(countriesMap)[0]] ||
                        "Select Country"}
                    </span>
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
                <Command>
                <div className="p-2">
                    <div className="border rounded-lg border-b-0 overflow-hidden">
                    <CommandInput placeholder="Search countries..." className="h-9" />
                    </div>
                </div>
                <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                    {Object.entries(countriesMap).map(([code, name]) => (
                        <CommandItem
                        key={code}
                        value={name}
                        onSelect={() => {
                            setSelectedCountry(code);
                            setCountryOpen(false);
                            // Reset timezone when country changes to default or null until new ones load
                             setSelectedTimezone(null);
                        }}
                        className="min-h-[40px] flex items-center gap-2"
                        >
                        <Check
                            className={cn(
                            "mr-2 h-4 w-4",
                            selectedCountry === code ? "opacity-100" : "opacity-0"
                            )}
                        />
                        <span className="text-lg inline-flex items-center justify-center w-6 h-4 rounded-full overflow-hidden">
                            {renderFlag(code)}
                        </span>
                        <span className="text-sm">{name}</span>
                        </CommandItem>
                    ))}
                    </CommandGroup>
                </CommandList>
                </Command>
            </PopoverContent>
        </Popover>

        <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
        <PopoverTrigger asChild>
            <Button
            variant="outline"
            role="combobox"
            aria-expanded={timezoneOpen}
            className="justify-between h-10  rounded-lg border-gray-200 hover:border-gray-300 bg-white dark:bg-[#0C0E12] dark:border-gray-200 dark:hover:border-gray-300 dark:text-white"
            >
            <div className="flex items-center gap-2">
                <Image
                src="/images/icons/untitledui/call-activity/time-icon.svg"
                alt="Timezone"
                className="w-4 h-4"
                />
                <span className="text-sm text-[#414651] dark:text-white font-semibold">
                {selectedTimezone ? (timezonesMap[selectedTimezone] || selectedTimezone) : "All"}
                </span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
            <Command>
            <div className="p-2">
                <div className="border rounded-lg border-b-0 overflow-hidden">
                <CommandInput placeholder="Search timezones..." className="h-9" />
                </div>
            </div>
            <CommandList>
                <CommandEmpty>No timezone found.</CommandEmpty>
                <CommandGroup>
                <CommandItem
                    value="All"
                    onSelect={() => {
                        setSelectedTimezone(null);
                        setTimezoneOpen(false);
                    }}
                    className="min-h-[40px] flex items-center gap-2"
                >
                    <Check
                        className={cn(
                        "mr-2 h-4 w-4",
                        !selectedTimezone ? "opacity-100" : "opacity-0"
                        )}
                    />
                    <Image
                        src="/images/icons/untitledui/call-activity/time-icon.svg"
                        alt="Timezone"
                        className="w-4 h-4"
                    />
                    <span className="text-sm">All</span>
                </CommandItem>
                {Object.entries(timezonesMap)
                  .filter(([code]) => code !== "All")
                  .map(([code, name]) => (
                    <CommandItem
                    key={code}
                    value={name}
                    onSelect={() => {
                        setSelectedTimezone(code);
                        setTimezoneOpen(false);
                    }}
                    className="min-h-[40px] flex items-center gap-2"
                    >
                    <Check
                        className={cn(
                        "mr-2 h-4 w-4",
                        selectedTimezone === code ? "opacity-100" : "opacity-0"
                        )}
                    />
                    <Image
                        src="/images/icons/untitledui/call-activity/time-icon.svg"
                        alt="Timezone"
                        className="w-4 h-4"
                    />
                    <span className="text-sm">{name}</span>
                    </CommandItem>
                ))}
                </CommandGroup>
            </CommandList>
            </Command>
        </PopoverContent>
        </Popover>
      </div>

      {/* Dynamic Graph */}
      <div className="bg-white dark:bg-[#181D27] border border-gray-200 dark:border-[#22262F] rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-6">Number of calls made per rep</h3>
        <div className="h-[400px] w-full">
            {graphData.length > 0 ? (
                 <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={graphData}
                        layout="vertical"
                        margin={{
                            top: 20,
                            right: 30,
                            left: 0,
                            bottom: 5,
                        }}
                        barSize={20}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E4E7EC" />
                        <XAxis 
                            type="number"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#667085', fontSize: 12 }}
                            tickMargin={10}
                        />
                        <YAxis 
                            dataKey="rep_name"
                            type="category"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: '#667085', fontSize: 12 }}
                            width={150}
                            tickMargin={10}
                            interval={0}
                        />
                        <Tooltip 
                            cursor={{ fill: 'transparent' }}
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                return (
                                    <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg">
                                      <div className="font-semibold mb-1">{payload[0].payload.rep_name}</div>
                                      <div>{payload[0].value} calls</div>
                                    </div>
                                );
                                }
                                return null;
                            }}
                        />
                        <Bar 
                            dataKey="call_volume" 
                            fill="#7F56D9" 
                            radius={[0, 4, 4, 0]}
                            activeBar={{ fill: "#6941C6" }}
                        />
                    </BarChart>
                </ResponsiveContainer>
            ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                    No data available for graph
                </div>
            )}
        </div>
      </div>

      {/* Rep Statistics Table */}
      <RepStatsTable 
        fetchFn={fetchRepStats}
        // Pass the global filters as `restObject` so they are passed to fetchFn
        restObject={{
            additionalQuery: apiQueryParams
        }}
        queryKey={["analytics_rep_stats_table", teamId || "workspace", selectedCountry, selectedTimezone]}
        onDataLoaded={(data) => {
            console.log("Rep stats loaded:", data);
            setGraphData(data?.data || []);
        }}
      />
    </div>
  );
}
